import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_arvaron-refresh/artifacts/mdrasy8y_17.png';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already subscribed');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const businessLinks = [
    { name: 'Arvaron Sourcing', path: '/business/sourcing' },
    { name: 'Arvaron Logistics', path: '/business/logistics' },
    { name: 'Arvaron Exports', path: '/business/exports' },
    { name: 'Arvaron Technologies', path: '/business/technologies' },
    { name: 'Arvaron Digital', path: '/business/digital' },
    { name: 'Arvaron Infra', path: '/business/infra' },
    { name: 'Arvaron Retail', path: '/business/retail' },
  ];

  const companyLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Careers', path: '/careers' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <footer className="bg-black text-white pt-20 pb-8 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Top brand divider */}
        <hr className="brand-hr mb-16 opacity-30" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Company Info */}
          <div>
            <img src={LOGO_URL} alt="Arvaron Group" className="h-10 w-auto mb-6" />
            <p className="text-white/40 text-sm mb-6 leading-relaxed">
              A premier Indian holding company driving innovation and scale across strategic verticals.
            </p>
            <p className="text-white/30 text-xs leading-relaxed">
              D/2, Rani Plaza, Andheri-Kurla Road<br />
              Sakinaka, Mumbai – 400072<br />
              India
            </p>
            <div className="mt-4 space-y-1">
              <a href="mailto:contact@arvaron.com" className="text-sm text-white/40 hover:text-[#17B9BE] transition-colors block">
                contact@arvaron.com
              </a>
              <a href="tel:+919833361155" className="text-sm text-white/40 hover:text-[#17B9BE] transition-colors block">
                +91 98333 61155
              </a>
            </div>
          </div>

          {/* Our Businesses */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold mb-6">Our Businesses</h4>
            <ul className="space-y-3">
              {businessLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-white/40 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold mb-6">Company</h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-white/40 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold mb-6">Stay Updated</h4>
            <p className="text-sm text-white/40 mb-6">
              Subscribe for industry insights and corporate updates.
            </p>
            <form onSubmit={handleNewsletterSubmit} data-testid="newsletter-form">
              <div className="flex flex-col space-y-3">
                <input
                  type="email"
                  data-testid="newsletter-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="px-4 py-3 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#17B9BE]/50 transition-colors"
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit-button"
                  disabled={isSubmitting}
                  className="px-4 py-3 brand-gradient-bg text-black rounded text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-xs text-white/30">
              &copy; {new Date().getFullYear()} Arvaron Group of Companies. All rights reserved.
            </p>
            <p className="text-xs text-white/20">
              Est. 2025 &middot; Mumbai, India
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
